import {
	AssignmentSetupType,
	Step,
	getStepsByType,
	getScheduledEditSteps
} from 'LearningManager/constants/createAssignment.constant';
import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { useStores } from 'Hooks/useStore';
import { AssignmentSource } from 'Services/Api/AssignmentSources/Types';

interface ConfigureState {
	assignmentName: string;
	certDuration: string;
	certExpirationDate: string;
	assignmentStart: string;
	assignmentStartDate: string;
	dueDate: string;
	dueDateValue: string;
	assignmentType: string;
}

// Full option objects are stored at selection time (not just ids) so the
// selection can be displayed anywhere without re-querying the fetched lists.
export interface SelectedGroupOption {
	id: number;
	name: string;
	label?: string;
}

export interface SelectedRoleOption {
	id: number;
	name: string;
}

export interface SelectedUserOption {
	id: string; // UUID
	firstName: string;
	lastName: string;
	email: string;
}

// Fields that hold the audience option objects (keyed by scope).
type AssignUsersOptionField =
	| 'selectedOrganizations'
	| 'selectedProperties'
	| 'selectedRoles'
	| 'selectedGroups';

interface CustomSetupState {
	selectedLearningPathCourseIds: number[];
	selectedIndividualCourseIds: number[];
	selectedAssignUsersOptionId: number | null;
	selectedOrganizations: SelectedGroupOption[];
	selectedProperties: SelectedGroupOption[];
	selectedRoles: SelectedRoleOption[];
	selectedRolePropertyIds: number[];
	selectedGroups: SelectedGroupOption[];
	selectedGroupPropertyIds: number[];
	selectedIndividualUsers: SelectedUserOption[];
	selectedIndividualPropertyIds: number[];
}

interface QuickSetupState {
	selectedLearningPathCourseIds: number[];
}

interface AssignmentDraft {
	configure: ConfigureState;
	quickSetup: QuickSetupState;
	customSetup: CustomSetupState;
}

const DEFAULT_DRAFT: AssignmentDraft = {
	configure: {
		assignmentName: '',
		certDuration: '',
		certExpirationDate: '',
		assignmentStart: '',
		assignmentStartDate: '',
		dueDate: '',
		dueDateValue: '',
		assignmentType: ''
	},
	quickSetup: {
		selectedLearningPathCourseIds: []
	},
	customSetup: {
		selectedLearningPathCourseIds: [],
		selectedIndividualCourseIds: [],
		selectedAssignUsersOptionId: null,
		selectedOrganizations: [],
		selectedProperties: [],
		selectedRoles: [],
		selectedRolePropertyIds: [],
		selectedGroups: [],
		selectedGroupPropertyIds: [],
		selectedIndividualUsers: [],
		selectedIndividualPropertyIds: []
	}
};

// Maps which scope key the API returns to the Assign Users option id.
// Note: the GET response only exposes the scope *keys* (not the selected ids),
// and 'groups' is shared by Organization/Property/Group — so the specific
// audience selections cannot be reconstructed here (see #5 limitation).
const scopeKeyToAssignUsersOptionId = (
	scopes: AssignmentSource['scopes']
): number | null => {
	if (scopes.includes('roles')) return 634;
	if (scopes.includes('users')) return 784;
	if (scopes.includes('groups') || scopes.includes('group_labels')) return 676;
	return null;
};

const mapAssignmentSourceToDraft = (
	source: AssignmentSource
): AssignmentDraft => {
	const certDuration =
		source.expiration_type === 'interval'
			? 'one-year'
			: source.expiration_type === 'date'
			? 'custom'
			: 'never';

	// expiration_date is stored as 'MM-DD'; rebuild a parseable date for the picker
	const certExpirationDate =
		source.expiration_type === 'date' && source.expiration_date
			? `${moment().format('YYYY')}-${source.expiration_date}`
			: '';

	const isFutureStart = source.start_date
		? moment(source.start_date).isAfter(moment(), 'day')
		: false;

	const learningPathIds = source.learning_paths.map(
		(lp) => lp.learning_path_id
	);
	const activityIds = source.activities.map((a) => a.activity_id);

	return {
		configure: {
			assignmentName: source.name,
			certDuration,
			certExpirationDate,
			assignmentStart: isFutureStart ? 'schedule' : 'immediate',
			assignmentStartDate: source.start_date ?? '',
			dueDate: source.due_date ? 'custom' : 'none',
			dueDateValue: source.due_date ?? '',
			assignmentType: source.is_assigned_to_future_users
				? 'ongoing-assignment'
				: 'one-time-assignment'
		},
		quickSetup: {
			selectedLearningPathCourseIds: learningPathIds
		},
		customSetup: {
			...DEFAULT_DRAFT.customSetup,
			selectedLearningPathCourseIds: learningPathIds,
			selectedIndividualCourseIds: activityIds,
			selectedAssignUsersOptionId: scopeKeyToAssignUsersOptionId(
				source.scopes
			)
		}
	};
};

interface StepperContextValue {
	current: number;
	steps: Step[];
	totalSteps: number;
	isFirst: boolean;
	isLast: boolean;
	setupType: AssignmentSetupType;
	isEditMode: boolean;
	isScheduledEdit: boolean;
	draft: AssignmentDraft;
	setSetupType: React.Dispatch<React.SetStateAction<AssignmentSetupType>>;
	updateDraft: (updater: (prev: AssignmentDraft) => AssignmentDraft) => void;
	updateConfigure: (data: Partial<ConfigureState>) => void;
	toggleQuickCourseSelection: (id: number) => void;
	toggleCustomCourseSelection: (
		field: 'selectedLearningPathCourseIds' | 'selectedIndividualCourseIds',
		id: number
	) => void;
	toggleAssignUsersSelection: (
		field: AssignUsersOptionField,
		item: SelectedGroupOption | SelectedRoleOption
	) => void;
	setAssignUsersSelection: (
		field: AssignUsersOptionField,
		items: (SelectedGroupOption | SelectedRoleOption)[]
	) => void;
	setAssignUsersPropertyFilter: (
		field:
			| 'selectedRolePropertyIds'
			| 'selectedGroupPropertyIds'
			| 'selectedIndividualPropertyIds',
		ids: number[]
	) => void;
	setSelectedAssignUsersOptionId: (id: number) => void;
	toggleIndividualUserSelection: (user: SelectedUserOption) => void;
	setIndividualUserSelection: (users: SelectedUserOption[]) => void;
	next: () => void;
	back: () => void;
	goTo: (step: number) => void;
}

const StepperContext = createContext<StepperContextValue | null>(null);

interface StepperProviderProps {
	children: React.ReactNode;
}

export const StepperProvider: React.FC<StepperProviderProps> = ({
	children
}) => {
	const { search } = useLocation();
	const { learningManagerStore } = useStores();
	const query = useMemo(() => new URLSearchParams(search), [search]);
	const currentSetupType = query.get(
		'setupType'
	) as AssignmentSetupType | null;
	const assignmentId = query.get('assignmentId');
	const isEditMode = Boolean(assignmentId || query.get('mode') === 'edit');
	const isScheduledEdit = query.get('isScheduled') === 'true';

	const [current, setCurrent] = useState(0);
	const [setupType, setSetupType] = useState<AssignmentSetupType>(
		currentSetupType ?? 'Quick Setup'
	);
	const [draft, setDraft] = useState<AssignmentDraft>(DEFAULT_DRAFT);
	const steps = isScheduledEdit
		? getScheduledEditSteps(setupType)
		: getStepsByType(setupType);
	const totalSteps = steps.length;

	useEffect(() => {
		setCurrent((prev) => Math.min(prev, totalSteps - 1));
	}, [totalSteps]);

	useEffect(() => {
		if (currentSetupType) {
			setSetupType(currentSetupType);
		}
		setCurrent(0);

		if (!assignmentId) {
			setDraft(DEFAULT_DRAFT);
			return;
		}

		// Edit mode: prefill the draft from the existing assignment source
		let cancelled = false;
		const id = Number(assignmentId);
		learningManagerStore
			.getAssignmentSource(id)
			.then(async (source) => {
				if (cancelled) return;
				setDraft(mapAssignmentSourceToDraft(source));

				// Reconstruct the audience selections from the scope detail
				// endpoints (the assignment-source response only carries the
				// scope keys, not the selected entities).
				const audience: Partial<CustomSetupState> = {};
				try {
					if (source.scopes.includes('roles')) {
						const roles = await learningManagerStore.getAssignmentSourceScopeRoles(
							id
						);
						audience.selectedRoles = roles.map((r) => ({
							id: r.id,
							name: r.name
						}));
					}
					if (source.scopes.includes('groups')) {
						const groups = await learningManagerStore.getAssignmentSourceScopeGroups(
							id
						);
						audience.selectedGroups = groups.map((g) => ({
							id: g.id,
							name: g.name
						}));
					}
					if (source.scopes.includes('group_labels')) {
						const labels = await learningManagerStore.getAssignmentSourceScopeGroupLabels(
							id
						);
						audience.selectedOrganizations = labels.map((l) => ({
							id: l.id,
							name: l.name
						}));
					}
					if (source.scopes.includes('users')) {
						const users = await learningManagerStore.getAssignmentSourceScopeUsers(
							id
						);
						audience.selectedIndividualUsers = users.map((u) => ({
							id: u.id,
							firstName: u.firstName,
							lastName: u.lastName,
							email: u.email
						}));
					}
				} catch {
					// leave selections empty if scope resolution fails
					return;
				}

				if (!cancelled && Object.keys(audience).length) {
					setDraft((prev) => ({
						...prev,
						customSetup: { ...prev.customSetup, ...audience }
					}));
				}
			})
			.catch(() => {
				if (!cancelled) {
					setDraft(DEFAULT_DRAFT);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [currentSetupType, assignmentId]);

	const next = () => setCurrent((prev) => Math.min(prev + 1, totalSteps - 1));
	const back = () => setCurrent((prev) => Math.max(prev - 1, 0));
	const goTo = (step: number) => {
		if (step >= 0 && step < totalSteps) setCurrent(step);
	};
	const updateDraft = (updater: (prev: AssignmentDraft) => AssignmentDraft) =>
		setDraft((prev) => updater(prev));
	const updateConfigure = (data: Partial<ConfigureState>) =>
		setDraft((prev) => ({
			...prev,
			configure: { ...prev.configure, ...data }
		}));
	const toggleQuickCourseSelection = (id: number) => {
		setDraft((prev) => {
			const selectedIds = prev.quickSetup.selectedLearningPathCourseIds;
			const nextIds = selectedIds.includes(id)
				? selectedIds.filter((item) => item !== id)
				: [...selectedIds, id];
			return {
				...prev,
				quickSetup: {
					...prev.quickSetup,
					selectedLearningPathCourseIds: nextIds
				}
			};
		});
	};
	const toggleCustomCourseSelection = (
		field: 'selectedLearningPathCourseIds' | 'selectedIndividualCourseIds',
		id: number
	) => {
		setDraft((prev) => {
			const selectedIds = prev.customSetup[field];
			const nextIds = selectedIds.includes(id)
				? selectedIds.filter((item) => item !== id)
				: [...selectedIds, id];
			return {
				...prev,
				customSetup: {
					...prev.customSetup,
					[field]: nextIds
				}
			};
		});
	};
	const setSelectedAssignUsersOptionId = (id: number) =>
		setDraft((prev) => ({
			...prev,
			customSetup: {
				...prev.customSetup,
				selectedAssignUsersOptionId: id
			}
		}));
	const toggleAssignUsersSelection = (
		field: AssignUsersOptionField,
		item: SelectedGroupOption | SelectedRoleOption
	) => {
		setDraft((prev) => {
			const selected = prev.customSetup[field] as Array<{ id: number }>;
			const nextItems = selected.some((s) => s.id === item.id)
				? selected.filter((s) => s.id !== item.id)
				: [...selected, item];
			return {
				...prev,
				customSetup: {
					...prev.customSetup,
					[field]: nextItems
				}
			};
		});
	};
	const setAssignUsersSelection = (
		field: AssignUsersOptionField,
		items: (SelectedGroupOption | SelectedRoleOption)[]
	) => {
		setDraft((prev) => ({
			...prev,
			customSetup: {
				...prev.customSetup,
				[field]: items
			}
		}));
	};
	const setAssignUsersPropertyFilter = (
		field:
			| 'selectedRolePropertyIds'
			| 'selectedGroupPropertyIds'
			| 'selectedIndividualPropertyIds',
		ids: number[]
	) => {
		setDraft((prev) => ({
			...prev,
			customSetup: {
				...prev.customSetup,
				[field]: ids
			}
		}));
	};

	const toggleIndividualUserSelection = (user: SelectedUserOption) => {
		setDraft((prev) => {
			const current = prev.customSetup.selectedIndividualUsers;
			const next = current.some((u) => u.id === user.id)
				? current.filter((u) => u.id !== user.id)
				: [...current, user];
			return {
				...prev,
				customSetup: { ...prev.customSetup, selectedIndividualUsers: next }
			};
		});
	};

	const setIndividualUserSelection = (users: SelectedUserOption[]) => {
		setDraft((prev) => ({
			...prev,
			customSetup: { ...prev.customSetup, selectedIndividualUsers: users }
		}));
	};

	return (
		<StepperContext.Provider
			value={{
				current,
				steps,
				totalSteps,
				isFirst: current === 0,
				isLast: current === totalSteps - 1,
				setupType,
				isEditMode,
				isScheduledEdit,
				draft,
				setSetupType,
				updateDraft,
				updateConfigure,
				toggleQuickCourseSelection,
				toggleCustomCourseSelection,
				toggleAssignUsersSelection,
				setAssignUsersSelection,
				setAssignUsersPropertyFilter,
				setSelectedAssignUsersOptionId,
				toggleIndividualUserSelection,
				setIndividualUserSelection,
				next,
				back,
				goTo
			}}
		>
			{children}
		</StepperContext.Provider>
	);
};

export const useStepper = (): StepperContextValue => {
	const context = useContext(StepperContext);
	if (!context) {
		throw new Error('useStepper must be used within a StepperProvider');
	}
	return context;
};
